import { Dropdown } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  SettingOutlined, 
  UserOutlined, 
  TeamOutlined, 
  ShoppingOutlined, 
  ShoppingCartOutlined, 
  CreditCardOutlined, 
  LogoutOutlined 
} from '@ant-design/icons';

import { useAuth, useOrders } from '@hooks';
import './styles.css';

interface UserMenuProps {
  profile?: any;
}

export const UserMenu = ({ profile }: UserMenuProps) => {
  const { logout } = useAuth();
  const { createPortalSession } = useOrders();
  const location = useLocation();

  const name = profile?.physicalPerson?.name;

  const handleAccessBillingPortal = async () => {
    try {
      // Save current location to return to it after billing portal
      const currentPath = location.pathname + location.search;
      await createPortalSession.mutateAsync(currentPath);
    } catch (error) {
      // Error message handled by hook
    }
  };

  return (
    <div className='flex'>
      <div className='w-9 xl:w-10 !h-9 xl:!h-10'>
        <Dropdown
          overlayClassName="user-menu-dropdown"
          menu={{
            items: [
              // Account Section
              {
                key: 'account-header',
                label: <div className="user-menu-section-header">Account</div>,
                disabled: true,
              },
              {
                key: 'settings',
                icon: <SettingOutlined className="user-menu-icon" />,
                label: <NavLink to='/settings' className="user-menu-item">Settings</NavLink>,
              },
              // Admin Section - only show if user is admin
              ...(profile?.profile === 'admin' ? [
                {
                  type: 'divider' as const,
                },
                {
                  key: 'admin-header',
                  label: <div className="user-menu-section-header">Administration</div>,
                  disabled: true,
                },
                {
                  key: 'users',
                  icon: <UserOutlined className="user-menu-icon" />,
                  label: <NavLink to='/users' className="user-menu-item">Users</NavLink>,
                },
                {
                  key: 'talent-pool',
                  icon: <TeamOutlined className="user-menu-icon" />,
                  label: <NavLink to='/talent-pool' className="user-menu-item">Talent Pool</NavLink>,
                },
                {
                  key: 'customer-leads',
                  icon: <TeamOutlined className="user-menu-icon" />,
                  label: <NavLink to='/customer-leads' className="user-menu-item">Customer Leads</NavLink>,
                },
                {
                  type: 'divider' as const,
                },
                {
                  key: 'commerce-header',
                  label: <div className="user-menu-section-header">Shop</div>,
                  disabled: true,
                },
                {
                  key: 'products',
                  icon: <ShoppingOutlined className="user-menu-icon" />,
                  label: <NavLink to='/products' className="user-menu-item">Products</NavLink>,
                },
                {
                  key: 'orders',
                  icon: <ShoppingCartOutlined className="user-menu-icon" />,
                  label: <NavLink to='/orders-management' className="user-menu-item">Orders</NavLink>,
                },
                {
                  key: 'billing',
                  icon: <CreditCardOutlined />,
                  label: 'Billing Portal',
                  onClick: handleAccessBillingPortal,
                },
              ] : [
                {
                  type: 'divider' as const,
                },
                {
                  key: 'commerce-header',
                  label: <div className="user-menu-section-header">Shop</div>,
                  disabled: true,
                },
                {
                  key: 'billing',
                  icon: <CreditCardOutlined />,
                  label: 'Billing Portal',
                  onClick: handleAccessBillingPortal,
                },
              ]),
              // Divider before logout
              {
                type: 'divider' as const,
              },
              // Logout Section
              {
                key: 'logout',
                icon: <LogoutOutlined className="user-menu-icon" />,
                label: <span className='user-menu-item logout-text'>Logout</span>,
                onClick: () => logout(),
              },
            ],
          }}
          placement='bottomRight'
          trigger={['click']}
        >
          <div className='w-full h-full bg-zinc-900 rounded-none border-2 border-primary cursor-pointer overflow-hidden flex items-center justify-center'>
            <span className='text-zinc-400 text-xl uppercase'>{name?.charAt(0) || '?'}</span>
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
